import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Save, Trash2, User } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_FIELDS = ["Age", "Body", "Gender / Pronouns", "Height", "Personality"];

export default function CharacterFactsPanel({ chatId, mainCharacterName }) {
  const [facts, setFacts] = useState({}); // { characterKey: {field: value} }
  const [tabs, setTabs] = useState([]); // ordered list of keys
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newTab, setNewTab] = useState("");
  const [showAddTab, setShowAddTab] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/chats/${chatId}/character-facts`).then(({ data }) => {
      if (!alive) return;
      const f = data.character_facts || {};
      // Ensure the main character key exists
      const keys = Object.keys(f);
      if (mainCharacterName && !keys.includes(mainCharacterName)) {
        f[mainCharacterName] = f[mainCharacterName] || {};
        keys.unshift(mainCharacterName);
      }
      setFacts(f);
      setTabs(Object.keys(f));
      setActive(keys[0] || mainCharacterName || null);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [chatId, mainCharacterName]);

  async function persistTab(key, entries) {
    try {
      const { data } = await api.put(`/chats/${chatId}/character-facts`, { character_key: key, facts: entries });
      setFacts(data.character_facts);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed.");
    }
  }

  async function removeTab(key) {
    if (!confirm(`Remove all facts for "${key}"?`)) return;
    try {
      const { data } = await api.delete(`/chats/${chatId}/character-facts/${encodeURIComponent(key)}`);
      setFacts(data.character_facts);
      const remaining = Object.keys(data.character_facts);
      setTabs(remaining);
      setActive(remaining[0] || null);
      toast.success("Removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  function addTab() {
    const name = newTab.trim();
    if (!name) return;
    if (facts[name] || tabs.includes(name)) { toast.error("That character already exists."); return; }
    setFacts((f) => ({ ...f, [name]: {} }));
    setTabs((t) => [...t, name]);
    setActive(name);
    setNewTab("");
    setShowAddTab(false);
  }

  if (loading) return <div className="text-xs text-neutral-500">Loading facts…</div>;

  return (
    <div>
      {/* Tabs row */}
      <div className="mb-3 flex items-center gap-1 overflow-x-auto pb-1" data-testid="facts-tabs">
        {tabs.map((k) => (
          <button
            key={k}
            data-testid={`facts-tab-${k}`}
            onClick={() => setActive(k)}
            className={`shrink-0 rounded-full px-3 py-1 text-xs transition ${
              active === k
                ? "bg-violet-500/25 border border-violet-500/40 text-violet-100"
                : "bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10"
            }`}
          >
            {k === mainCharacterName ? `★ ${k}` : k}
          </button>
        ))}
        <button
          data-testid="facts-add-tab-btn"
          onClick={() => setShowAddTab(true)}
          className="shrink-0 flex items-center gap-1 rounded-full bg-white/5 border border-dashed border-white/15 px-3 py-1 text-xs text-neutral-400 hover:text-white"
        >
          <Plus size={11} /> Character
        </button>
      </div>

      {showAddTab && (
        <div className="mb-3 flex gap-2">
          <input
            data-testid="facts-new-tab-input"
            autoFocus
            value={newTab}
            onChange={(e) => setNewTab(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTab()}
            placeholder="e.g. Mira, Ghost, my dog…"
            className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white outline-none focus:border-violet-500/50"
          />
          <button data-testid="facts-add-tab-save" onClick={addTab} className="rounded-lg bg-violet-500/25 px-3 py-1.5 text-xs text-violet-100">Add</button>
          <button onClick={() => { setNewTab(""); setShowAddTab(false); }} className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-neutral-400">Cancel</button>
        </div>
      )}

      {active && (
        <FactsEditor
          characterKey={active}
          initial={facts[active] || {}}
          onSave={(entries) => persistTab(active, entries)}
          onRemoveTab={active !== mainCharacterName ? () => removeTab(active) : null}
          isMain={active === mainCharacterName}
        />
      )}
    </div>
  );
}

function FactsEditor({ characterKey, initial, onSave, onRemoveTab, isMain }) {
  // entries: { fieldName: value } — start with default fields merged with initial
  const [entries, setEntries] = useState({});
  const [newField, setNewField] = useState("");

  useEffect(() => {
    const merged = {};
    for (const f of DEFAULT_FIELDS) merged[f] = initial[f] || "";
    for (const [k, v] of Object.entries(initial)) merged[k] = v;
    setEntries(merged);
  }, [characterKey, initial]);

  function updateField(k, v) {
    setEntries((e) => ({ ...e, [k]: v }));
  }
  function removeField(k) {
    setEntries((e) => {
      const c = { ...e }; delete c[k]; return c;
    });
  }
  function addField() {
    const k = newField.trim();
    if (!k) return;
    if (entries[k] !== undefined) return;
    setEntries((e) => ({ ...e, [k]: "" }));
    setNewField("");
  }
  function save() {
    // Strip empty
    const clean = Object.fromEntries(Object.entries(entries).filter(([k, v]) => k && v && String(v).trim()));
    onSave(clean);
    toast.success("Facts saved.");
  }

  const keys = Object.keys(entries);

  return (
    <div className="space-y-2" data-testid={`facts-editor-${characterKey}`}>
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-500/20 text-violet-200"><User size={12} /></span>
        <div className="flex-1 text-sm text-white font-medium">
          {characterKey}{isMain && <span className="ml-2 text-[10px] uppercase tracking-widest text-violet-300">Main</span>}
        </div>
      </div>

      <ul className="space-y-1.5">
        {keys.map((k) => (
          <li key={k} className="grid grid-cols-[110px_1fr_auto] items-center gap-2">
            <div className="text-[11px] text-neutral-400 truncate">{k}</div>
            <input
              data-testid={`facts-field-${characterKey}-${k}`}
              value={entries[k]}
              onChange={(e) => updateField(k, e.target.value)}
              placeholder="—"
              className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white outline-none focus:border-violet-500/50"
            />
            {!DEFAULT_FIELDS.includes(k) ? (
              <button onClick={() => removeField(k)} className="text-neutral-500 hover:text-rose-300" data-testid={`facts-remove-${characterKey}-${k}`}>
                <X size={12} />
              </button>
            ) : <span className="w-3" />}
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-2 pt-1">
        <input
          data-testid={`facts-new-field-${characterKey}`}
          value={newField}
          onChange={(e) => setNewField(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addField())}
          placeholder="Add custom field…"
          className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white outline-none focus:border-violet-500/50"
        />
        <button data-testid={`facts-add-field-${characterKey}`} onClick={addField} className="rounded-lg bg-white/5 px-2.5 py-1.5 text-xs text-neutral-300 hover:bg-white/10">
          <Plus size={12} />
        </button>
      </div>

      <div className="flex gap-2 pt-1">
        <button data-testid={`facts-save-${characterKey}`} onClick={save} className="btn-pill btn-primary flex-1 text-xs py-1.5">
          <Save size={12} /> Save facts
        </button>
        {onRemoveTab && (
          <button data-testid={`facts-remove-tab-${characterKey}`} onClick={onRemoveTab} className="rounded-full bg-rose-500/10 border border-rose-500/30 px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-500/15">
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}
